import React, { useState, useRef } from 'react';
import { ChevronLeft, ChevronRight, Volume2, VolumeX, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const DEAPActivite3Presentation = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizScore, setQuizScore] = useState(0);
  const audioRef = useRef(null);

  const slides = [
    // Slide 1 - Titre
    {
      type: 'title',
      title: "Activité 3",
      subtitle: "Observation de la personne et mesure des paramètres vitaux liés à son état de santé",
      gradient: "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 50%, #1E40AF 100%)"
    },
    // Slide 2 - Sommaire
    {
      type: 'sommaire',
      title: "Sommaire",
      sections: [
        { num: "1", title: "Paramètres à mesurer", items: ["Température", "Tension artérielle", "Fréquence cardiaque", "Saturation O2", "Fréquence respiratoire", "Glycémie"], color: "#DC2626" },
        { num: "2", title: "Paramètres à observer", items: ["État cutané", "Poids", "Selles", "Urines", "Douleur"], color: "#059669" },
        { num: "3", title: "Vocabulaire médical", items: ["Appareil urinaire", "Appareil cardio-vasculaire", "Appareil digestif", "Dermatologie", "Appareil respiratoire"], color: "#7C3AED" }
      ]
    },
    // Slide 3 - Introduction
    {
      type: 'section',
      title: "Introduction",
      gradient: "linear-gradient(135deg, #0EA5E9 0%, #0284C7 100%)"
    },
    // Slide 4 - Introduction contenu
    {
      type: 'content',
      title: "Les paramètres vitaux",
      content: "Les paramètres vitaux sont des signes essentiels qui permettent d'évaluer rapidement l'état de santé d'un enfant. Ils donnent des informations sur le fonctionnement du cœur, des poumons, de la température, du sucre dans le sang et du bien-être général.",
      highlight: "Cette fiche rassemble les 6 paramètres à mesurer et les 5 paramètres à observer pour surveiller efficacement un enfant, détecter rapidement un problème et agir en conséquence.",
      image: "https://images.pexels.com/photos/33919289/pexels-photo-33919289.jpeg?auto=compress&cs=tinysrgb&w=600"
    },
    // Slide 5 - Section À MESURER
    {
      type: 'section',
      title: "PARAMÈTRES À MESURER",
      subtitle: "6 paramètres essentiels",
      gradient: "linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)"
    },
    // Slide 6 - Température
    {
      type: 'content',
      title: "1. Température corporelle",
      definition: "Température moyenne des organes d'un être vivant.",
      content: "Méthodes de mesure : thermomètre auriculaire, axillaire ou frontal.",
      warning: "La prise rectale n'est plus recommandée car trop intrusive.",
      image: "https://images.pexels.com/photos/4021186/pexels-photo-4021186.jpeg?auto=compress&cs=tinysrgb&w=600"
    },
    // Slide 6 - Température valeurs
    {
      type: 'table',
      title: "Température - Valeurs normales",
      tableData: [
        { label: "Normale", value: "36,5°C – 37,5°C", color: "#22C55E" },
        { label: "Fébricule", value: "37,5°C – 38,0°C", color: "#F59E0B" },
        { label: "Hyperthermie", value: "> 38,0°C", color: "#EF4444" },
        { label: "Hypothermie", value: "< 36,0°C", color: "#3B82F6" }
      ],
      risks: [
        "Hyperthermie : convulsions, déshydratation",
        "Hypothermie : ralentissement des organes vitaux pouvant entraîner le décès"
      ]
    },
    // Slide 7 - Tension artérielle
    {
      type: 'content',
      title: "2. Tension artérielle",
      definition: "Pression du sang contre les parois des artères.",
      content: "Méthode de mesure : tensiomètre électronique ou manuel.",
      extraInfo: [
        { term: "Systole", desc: "Pression maximale lors de la contraction du cœur (premier chiffre)" },
        { term: "Diastole", desc: "Pression minimale lors du relâchement du cœur (second chiffre)" },
        { term: "mmHg", desc: "Millimètres de mercure - unité de mesure de la pression artérielle" }
      ],
      image: "https://images.pexels.com/photos/32417922/pexels-photo-32417922.jpeg?auto=compress&cs=tinysrgb&w=600"
    },
    // Slide 8 - Tension valeurs
    {
      type: 'table',
      title: "Tension artérielle - Valeurs normales",
      subtitle: "Systole / Diastole en mmHg (millimètres de mercure)",
      tableData: [
        { label: "Nourrissons (0-1 an)", value: "Systole 70-100 / Diastole 50-65 mmHg", color: "#EC4899" },
        { label: "Enfants (1-5 ans)", value: "Systole 80-110 / Diastole 55-70 mmHg", color: "#8B5CF6" },
        { label: "Enfants (6-12 ans)", value: "Systole 90-120 / Diastole 60-75 mmHg", color: "#3B82F6" }
      ],
      alerts: [
        "Au-dessus des normes : hypertension",
        "En dessous des normes : hypotension"
      ]
    },
    // Slide 9 - Fréquence cardiaque
    {
      type: 'content',
      title: "3. Fréquence cardiaque / Pouls",
      definition: "Nombre de battements cardiaques par minute.",
      content: "Méthode de mesure : tensiomètre, saturomètre ou manuellement sur l'artère radiale.",
      image: "https://images.unsplash.com/photo-1623658045230-605cb00c80d6?crop=entropy&cs=srgb&fm=jpg&w=600"
    },
    // Slide 10 - Fréquence cardiaque valeurs
    {
      type: 'table',
      title: "Fréquence cardiaque - Valeurs normales",
      tableData: [
        { label: "Nourrissons (0-1 an)", value: "100-160 bpm", color: "#EC4899" },
        { label: "Enfants (1-5 ans)", value: "80-120 bpm", color: "#8B5CF6" },
        { label: "Enfants (6-12 ans)", value: "70-110 bpm", color: "#3B82F6" }
      ],
      alerts: [
        "Au-dessus des normes : tachycardie",
        "En dessous des normes : bradycardie"
      ]
    },
    // Slide 11 - Saturation
    {
      type: 'content',
      title: "4. Saturation en oxygène",
      definition: "Pourcentage d'oxygène dans le sang.",
      content: "Méthode de mesure : saturomètre sur l'index ou le lobe de l'oreille.",
      warning: "Attention aux mains froides ou au vernis qui peuvent fausser la mesure.",
      image: "https://images.pexels.com/photos/7580249/pexels-photo-7580249.jpeg?auto=compress&cs=tinysrgb&w=600"
    },
    // Slide 12 - Saturation valeurs
    {
      type: 'table',
      title: "Saturation - Valeurs normales",
      tableData: [
        { label: "Normale", value: "95% - 100%", color: "#22C55E" },
        { label: "Hypoxémie", value: "< 95%", color: "#F59E0B" },
        { label: "Critique", value: "< 90%", color: "#EF4444" }
      ]
    },
    // Slide 13 - Fréquence respiratoire
    {
      type: 'content',
      title: "5. Fréquence respiratoire",
      definition: "Nombre de mouvements respiratoires par minute (inspiration + expiration).",
      content: "Méthode de mesure : main sur le thorax + chronomètre.",
      image: "https://images.pexels.com/photos/32351204/pexels-photo-32351204.jpeg?auto=compress&cs=tinysrgb&w=600"
    },
    // Slide 14 - Fréquence respiratoire valeurs
    {
      type: 'table',
      title: "Fréquence respiratoire - Valeurs normales",
      tableData: [
        { label: "Nourrissons (0-1 an)", value: "30-60 mvts/min", color: "#EC4899" },
        { label: "Enfants (1-5 ans)", value: "20-30 mvts/min", color: "#8B5CF6" },
        { label: "Enfants (6-12 ans)", value: "15-25 mvts/min", color: "#3B82F6" }
      ],
      alerts: [
        "Au-dessus des normes : tachypnée",
        "En dessous des normes : bradypnée"
      ]
    },
    // Slide 15 - Signes détresse respiratoire
    {
      type: 'list',
      title: "Signes de détresse respiratoire",
      items: [
        { term: "Creux sus-sternal", desc: "Un creux qui se forme au niveau de la gorge lorsque l'enfant a du mal à respirer" },
        { term: "Tirage xiphoïdien", desc: "Un creux qui se forme au niveau du bas du sternum, juste au-dessus du ventre" },
        { term: "Balancement thoraco-abdominal", desc: "Le ventre se gonfle pendant que la poitrine se rétracte à l'inspiration" },
        { term: "Tirage intercostal", desc: "La peau entre les côtes est « aspirée » vers l'intérieur à chaque inspiration" }
      ],
      alertColor: "#EF4444"
    },
    // Slide 16 - Glycémie
    {
      type: 'content',
      title: "6. Glycémie capillaire",
      definition: "Taux de sucre (glucose) dans le sang, exprimé en grammes par litre (g/L).",
      content: "Méthode de mesure : à l'aide d'un glucomètre.",
      image: "https://images.pexels.com/photos/33200678/pexels-photo-33200678.jpeg?auto=compress&cs=tinysrgb&w=600"
    },
    // Slide 17 - Glycémie procédure
    {
      type: 'list',
      title: "Glycémie - Points importants",
      items: [
        { term: "Hygiène soignant", desc: "Se laver les mains et porter des gants (risque d'exposition au sang)" },
        { term: "Hygiène enfant", desc: "Mains propres et désinfectées pour ne pas fausser le résultat" },
        { term: "Zone de piqûre", desc: "Sur le côté du majeur, annulaire ou auriculaire" },
        { term: "À éviter", desc: "Jamais sur le pouce ni l'index (pour éviter la désensibilisation)" }
      ],
      alertColor: "#3B82F6"
    },
    // Slide 18 - Glycémie valeurs
    {
      type: 'table',
      title: "Glycémie - Valeurs normales",
      tableData: [
        { label: "À jeun", value: "0,70 – 1,10 g/L", color: "#22C55E" },
        { label: "2h30 après repas", value: "≤ 1,40 g/L", color: "#22C55E" },
        { label: "Hyperglycémie", value: "> 1,10 g/L à jeun", color: "#EF4444" },
        { label: "Hypoglycémie", value: "< 0,70 g/L", color: "#F59E0B" }
      ],
      alerts: [
        "Hyperglycémie : soif intense, besoin fréquent d'uriner, risque de déshydratation",
        "Hypoglycémie : faim, tremblements, sueurs, fatigue, confusion, parfois convulsions ou coma"
      ]
    },
    // Slide 19 - Section À OBSERVER
    {
      type: 'section',
      title: "PARAMÈTRES À OBSERVER",
      subtitle: "5 paramètres essentiels",
      gradient: "linear-gradient(135deg, #059669 0%, #047857 100%)"
    },
    // Slide 20 - État cutané
    {
      type: 'list',
      title: "1. L'état cutané",
      items: [
        { term: "Observer", desc: "Rougeurs, plaies, hématomes, dermabrasions" },
        { term: "Prévention", desc: "Surveillance des escarres" }
      ],
      alertColor: "#F59E0B"
    },
    // Slide 21 - Poids
    {
      type: 'content',
      title: "2. Le poids",
      content: "Surveillance régulière du poids pour prévenir dénutrition ou obésité.",
      extraInfo: [
        { term: "En maternité", desc: "Pesée tous les jours avec traçabilité. Sortie possible si pas perdu plus de 10% du poids de naissance ET courbe de poids qui remonte" },
        { term: "En crèche", desc: "Pesée tous les mois avec feuille de traçabilité" },
        { term: "IMC", desc: "Indice de Masse Corporelle - indicateur de corpulence" },
        { term: "Calcul IMC", desc: "Poids (kg) ÷ Taille² (m²) = IMC (kg/m²)" }
      ],
      image: "https://images.pexels.com/photos/11947830/pexels-photo-11947830.jpeg?auto=compress&cs=tinysrgb&w=600"
    },
    // Slide 22 - Selles
    {
      type: 'list',
      title: "3. Les selles",
      items: [
        { term: "Surveillance", desc: "Fréquence et aspect des selles" },
        { term: "Selles noires", desc: "Traitement au fer ou méléna (sang digéré, risque d'hémorragie interne)" },
        { term: "Selles rouge vif", desc: "Plaie anale ou hémorroïdes" }
      ],
      alertColor: "#8B5CF6"
    },
    // Slide 23 - Urines
    {
      type: 'table',
      title: "4. Les urines / Diurèse",
      subtitle: "Mesure sur 24h - Norme : 800 ml – 2 L / 24h",
      tableData: [
        { label: "Polyurie", value: "> 2 L / 24h", color: "#3B82F6" },
        { label: "Oligurie", value: "< 800 ml / 24h", color: "#F59E0B" },
        { label: "Pollakiurie", value: "> 7 mictions/jour", color: "#8B5CF6" },
        { label: "Anurie", value: "< 100 ml / 24h", color: "#EF4444" },
        { label: "Dysurie", value: "Difficulté à uriner", color: "#EC4899" }
      ]
    },
    // Slide 24 - Douleur
    {
      type: 'list',
      title: "5. L'évaluation de la douleur",
      items: [
        { term: "Échelle numérique (EN)", desc: "0 (pas de douleur) à 10 (douleur insupportable)" },
        { term: "Échelle ALGO PLUS", desc: "5 items, utilisée pour enfants non communicants ou en soins palliatifs" },
        { term: "Quand évaluer ?", desc: "Avant, pendant et après le soin" }
      ],
      alertColor: "#EF4444"
    },
    // Slide 25 - Récapitulatif
    {
      type: 'recap',
      title: "Récapitulatif - Paramètres vitaux",
      mesurer: ["Température", "Tension artérielle", "Fréquence cardiaque", "Saturation O2", "Fréquence respiratoire", "Glycémie"],
      observer: ["État cutané", "Poids", "Selles", "Urines", "Douleur"]
    },
    // Slide 26 - Section Vocabulaire médical
    {
      type: 'section',
      title: "VOCABULAIRE MÉDICAL",
      subtitle: "Terminologie par appareil",
      gradient: "linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)"
    },
    // Slide 27 - Préfixes et suffixes
    {
      type: 'vocabulary',
      title: "Préfixes et suffixes médicaux",
      columns: [
        {
          title: "Préfixes",
          items: [
            { term: "An- / A-", desc: "Absence (anurie, apnée)" },
            { term: "Poly-", desc: "Beaucoup (polyurie)" },
            { term: "Tachy-", desc: "Rapide (tachycardie)" },
            { term: "Brady-", desc: "Lent (bradycardie)" },
            { term: "Dys-", desc: "Difficulté (dysurie)" },
            { term: "Hyper-", desc: "Excès (hypertension)" },
            { term: "Hypo-", desc: "Insuffisance (hypotension)" },
            { term: "Héma-/Hémo-", desc: "Sang (hématurie)" }
          ]
        },
        {
          title: "Suffixes",
          items: [
            { term: "-ite", desc: "Inflammation (cystite)" },
            { term: "-urie", desc: "Urine (polyurie)" },
            { term: "-pnée", desc: "Respiration (apnée)" },
            { term: "-émèse", desc: "Vomissement" },
            { term: "-ectomie", desc: "Ablation chirurgicale" }
          ]
        },
        {
          title: "Racines",
          items: [
            { term: "Gastro-", desc: "Estomac" },
            { term: "Néphro-", desc: "Rein" },
            { term: "Cysto-", desc: "Vessie" },
            { term: "Broncho-", desc: "Bronches" },
            { term: "Pneumo-", desc: "Poumon" }
          ]
        }
      ]
    },
    // Slide 28 - Appareil urinaire
    {
      type: 'medicalVocab',
      title: "1. Appareil Urinaire",
      color: "#F59E0B",
      anatomy: "Reins → Uretères → Vessie → Urètre → Méat urinaire",
      terms: [
        { term: "Diurèse", def: "Volume d'urines sur 24h" },
        { term: "Oligurie", def: "Diminution des urines" },
        { term: "Polyurie", def: "Augmentation des urines" },
        { term: "Anurie", def: "Absence d'urines (urgence)" },
        { term: "Pollakiurie", def: "Mictions fréquentes" },
        { term: "Dysurie", def: "Difficulté à uriner" },
        { term: "Rétention urinaire", def: "Vessie pleine sans miction" },
        { term: "Incontinence", def: "Perte involontaire d'urine" }
      ],
      anomalies: [
        { term: "Hématurie", def: "Sang dans les urines" },
        { term: "Protéinurie", def: "Protéines dans urines" },
        { term: "Glycosurie", def: "Sucre dans urines" },
        { term: "Pyurie", def: "Pus dans urines" }
      ],
      pathologies: ["Cystite (infection vessie)", "Pyélonéphrite (infection rein)", "Néphrite (inflammation rein)", "Lithiase (calcul urinaire)", "Colique néphrétique"]
    },
    // Slide 29 - Appareil cardio-vasculaire
    {
      type: 'medicalVocab',
      title: "2. Appareil Cardio-Vasculaire",
      color: "#EF4444",
      anatomy: "Cœur (oreillettes + ventricules) → Artères → Veines → Capillaires",
      terms: [
        { term: "Pouls", def: "Battements artériels" },
        { term: "Bradycardie", def: "Rythme cardiaque lent" },
        { term: "Tachycardie", def: "Rythme cardiaque rapide" },
        { term: "Arythmie", def: "Rythme irrégulier" },
        { term: "Hypertension", def: "Tension artérielle élevée" },
        { term: "Hypotension", def: "Tension artérielle basse" }
      ],
      anomalies: [
        { term: "AVC", def: "Accident Vasculaire Cérébral" },
        { term: "AIT", def: "AVC transitoire" },
        { term: "Infarctus", def: "Nécrose du muscle cardiaque" },
        { term: "Thrombose", def: "Caillot sanguin" }
      ],
      pathologies: ["Embolie (migration caillot)", "Phlébite (inflammation veine)", "Insuffisance cardiaque"]
    },
    // Slide 30 - Appareil digestif
    {
      type: 'medicalVocab',
      title: "3. Appareil Digestif",
      color: "#22C55E",
      anatomy: "Bouche → Œsophage → Estomac → Intestin grêle → Côlon → Rectum → Anus",
      terms: [
        { term: "Diarrhée", def: "Selles liquides" },
        { term: "Constipation", def: "Retard des selles" },
        { term: "Vomissement", def: "Rejet du contenu gastrique" },
        { term: "Nausée", def: "Envie de vomir" },
        { term: "Régurgitation", def: "Reflux sans effort" },
        { term: "Occlusion", def: "Blocage intestinal" }
      ],
      anomalies: [
        { term: "Hématémèse", def: "Vomissement de sang" },
        { term: "Rectorragie", def: "Sang rouge par l'anus" },
        { term: "Méléna", def: "Sang noir digéré (selles)" },
        { term: "Fécalome", def: "Bouchon de selles" }
      ],
      pathologies: ["Gastrite (inflammation estomac)", "Appendicite", "Cirrhose (maladie du foie)", "Péritonite"]
    },
    // Slide 31 - Appareil digestif - Métabolisme
    {
      type: 'list',
      title: "Métabolisme - Glycémie",
      items: [
        { term: "Glycémie", desc: "Taux de sucre dans le sang" },
        { term: "Hypoglycémie", desc: "Sucre trop bas (< 0,70 g/L) - tremblements, sueurs, confusion" },
        { term: "Hyperglycémie", desc: "Sucre trop haut (> 1,10 g/L à jeun) - soif intense, fatigue" },
        { term: "Insuline", desc: "Hormone du pancréas qui régule la glycémie" },
        { term: "Polyphagie", desc: "Faim excessive (signe de diabète)" },
        { term: "Polydipsie", desc: "Soif excessive (signe de diabète)" }
      ],
      alertColor: "#22C55E"
    },
    // Slide 32 - Dermatologie
    {
      type: 'medicalVocab',
      title: "4. Dermatologie",
      color: "#EC4899",
      anatomy: "Épiderme → Derme → Hypoderme",
      terms: [
        { term: "Érythème", def: "Rougeur cutanée" },
        { term: "Escarre", def: "Plaie de pression" },
        { term: "Œdème", def: "Gonflement (rétention eau)" },
        { term: "Cyanose", def: "Coloration bleue (manque O2)" },
        { term: "Ictère", def: "Coloration jaune (foie)" },
        { term: "Prurit", def: "Démangeaison" }
      ],
      anomalies: [
        { term: "Mycose", def: "Infection par champignon" },
        { term: "Abcès", def: "Poche de pus" },
        { term: "Vésicule", def: "Petite cloque" },
        { term: "Ulcère", def: "Perte de substance cutanée" }
      ],
      pathologies: ["Rôle AP : surveiller la peau, prévenir les escarres, observer les changements de coloration"]
    },
    // Slide 33 - Appareil respiratoire
    {
      type: 'medicalVocab',
      title: "5. Appareil Respiratoire",
      color: "#0EA5E9",
      anatomy: "Nez → Trachée → Bronches → Poumons → Alvéoles",
      terms: [
        { term: "Dyspnée", def: "Difficulté à respirer" },
        { term: "Tachypnée", def: "Respiration rapide" },
        { term: "Bradypnée", def: "Respiration lente" },
        { term: "Apnée", def: "Arrêt respiratoire" },
        { term: "Cyanose", def: "Manque d'oxygène" }
      ],
      anomalies: [
        { term: "Bronchite", def: "Inflammation des bronches" },
        { term: "Pneumonie", def: "Infection du poumon" },
        { term: "Hémoptysie", def: "Sang dans les crachats" },
        { term: "Embolie pulmonaire", def: "Caillot dans le poumon" }
      ],
      pathologies: ["OAP (œdème aigu pulmonaire)", "Pneumothorax (air dans plèvre)", "Asphyxie"]
    },
    // Slide 34 - Récapitulatif vocabulaire
    {
      type: 'vocabRecap',
      title: "Récapitulatif - Vocabulaire médical",
      categories: [
        { name: "Urinaire", terms: ["Anurie", "Polyurie", "Dysurie", "Hématurie", "Cystite"], color: "#F59E0B" },
        { name: "Cardio", terms: ["Tachycardie", "Bradycardie", "AVC", "Infarctus", "Hypertension"], color: "#EF4444" },
        { name: "Digestif", terms: ["Hématémèse", "Méléna", "Gastrite", "Hypoglycémie"], color: "#22C55E" },
        { name: "Dermato", terms: ["Érythème", "Escarre", "Cyanose", "Ictère", "Prurit"], color: "#EC4899" },
        { name: "Respi", terms: ["Dyspnée", "Apnée", "Pneumonie", "Hémoptysie"], color: "#0EA5E9" }
      ]
    },
    // Slide 35 - Félicitations
    {
      type: 'congratulations',
      title: "Félicitations !",
      message: "Vous avez terminé l'Activité 3",
      subtitle: "Observation de la personne et mesure des paramètres vitaux liés à son état de santé",
      details: [
        "6 paramètres à mesurer maîtrisés",
        "5 paramètres à observer compris",
        "Vocabulaire médical acquis"
      ],
      nextStep: "Passez maintenant au quiz pour valider vos connaissances !"
    }
  ];

  // Quiz questions - 30 questions
  const quizQuestions = [
    {
      id: 'q1',
      question: "Que signifie le terme « Anurie » ?",
      options: ["Augmentation de la quantité d'urine", "Absence d'urine", "Sang dans les urines", "Urine fréquente mais peu abondante"],
      correct: 1,
      type: 'single'
    },
    {
      id: 'q2',
      question: "La cystite est :",
      options: ["Inflammation de la vessie", "Inflammation du rein", "Présence de sucre dans les urines", "Absence de miction"],
      correct: 0,
      type: 'single'
    },
    {
      id: 'q3',
      question: "Le terme « Polyurie » désigne :",
      options: ["Difficulté à uriner", "Augmentation du volume des urines", "Sang dans les urines", "Absence d'urine"],
      correct: 1,
      type: 'single'
    },
    {
      id: 'q4',
      question: "Que signifie « Hématurie » ?",
      options: ["Sang dans les urines", "Pus dans les urines", "Sucre dans les urines", "Albumine dans les urines"],
      correct: 0,
      type: 'single'
    },
    {
      id: 'q5',
      question: "La dysurie correspond à :",
      options: ["Absence d'urine", "Difficulté à uriner", "Urine abondante", "Sang dans les urines"],
      correct: 1,
      type: 'single'
    },
    {
      id: 'q6',
      question: "Quelle est la fonction d'une artère ?",
      options: ["Ramener le sang au cœur", "Transporter le sang vers les organes", "Transporter la lymphe", "Éliminer les déchets"],
      correct: 1,
      type: 'single'
    },
    {
      id: 'q7',
      question: "Un infarctus correspond à :",
      options: ["Une inflammation du myocarde", "Une mort tissulaire par défaut d'oxygène", "Une accélération du rythme cardiaque", "Un caillot dans une veine"],
      correct: 1,
      type: 'single'
    },
    {
      id: 'q8',
      question: "La tachycardie correspond à :",
      options: ["Un ralentissement du cœur", "Un pouls irrégulier", "Une accélération du cœur", "Une inflammation du cœur"],
      correct: 2,
      type: 'single'
    },
    {
      id: 'q9',
      question: "Que signifie le sigle AVC ?",
      options: ["Accident Vasculaire Cardiaque", "Accident Vasculaire Cérébral", "Arrêt Vasculaire Coronarien", "Arrêt Ventriculaire Cardiaque"],
      correct: 1,
      type: 'single'
    },
    {
      id: 'q10',
      question: "L'hypertension correspond à :",
      options: ["Une tension artérielle trop basse", "Une tension artérielle trop élevée", "Un pouls irrégulier", "Un arrêt cardiaque"],
      correct: 1,
      type: 'single'
    },
    {
      id: 'q11',
      question: "L'anorexie est :",
      options: ["Un besoin excessif de manger", "Une perte d'appétit", "Un vomissement glaireux", "Une inflammation de l'estomac"],
      correct: 1,
      type: 'single'
    },
    {
      id: 'q12',
      question: "La boulimie se définit par :",
      options: ["Refus de s'alimenter", "Sensation excessive de faim", "Douleur au niveau de l'estomac", "Vomissement de sang"],
      correct: 1,
      type: 'single'
    },
    {
      id: 'q13',
      question: "La gastrite est :",
      options: ["Inflammation de l'intestin", "Inflammation de l'estomac", "Ablation de l'estomac", "Douleur intestinale"],
      correct: 1,
      type: 'single'
    },
    {
      id: 'q14',
      question: "Une appendicite est :",
      options: ["Inflammation de l'appendice", "Ablation de l'appendice", "Occlusion intestinale", "Tumeur du caecum"],
      correct: 0,
      type: 'single'
    },
    {
      id: 'q15',
      question: "La cirrhose est une maladie :",
      options: ["Du rein", "Du foie", "De l'intestin", "De la vésicule biliaire"],
      correct: 1,
      type: 'single'
    },
    {
      id: 'q16',
      question: "La dyspnée correspond à :",
      options: ["Respiration rapide", "Difficulté à respirer", "Arrêt de la respiration", "Respiration lente"],
      correct: 1,
      type: 'single'
    },
    {
      id: 'q17',
      question: "La tachypnée signifie :",
      options: ["Respiration rapide", "Respiration lente", "Arrêt momentané de la respiration", "Douleur thoracique"],
      correct: 0,
      type: 'single'
    },
    {
      id: 'q18',
      question: "L'apnée est :",
      options: ["Une toux persistante", "Une difficulté respiratoire", "Un arrêt momentané de la respiration", "Une respiration accélérée"],
      correct: 2,
      type: 'single'
    },
    {
      id: 'q19',
      question: "La bronchite est :",
      options: ["Inflammation de la gorge", "Inflammation des amygdales", "Inflammation des bronches", "Inflammation de la plèvre"],
      correct: 2,
      type: 'single'
    },
    {
      id: 'q20',
      question: "Une pneumonie est :",
      options: ["Infection du foie", "Infection du poumon", "Infection des reins", "Infection de la gorge"],
      correct: 1,
      type: 'single'
    },
    {
      id: 'q21',
      question: "Un abcès est :",
      options: ["Une rougeur de la peau", "Une démangeaison", "Un amas de pus", "Une tumeur cancéreuse"],
      correct: 2,
      type: 'single'
    },
    {
      id: 'q22',
      question: "L'érythème est :",
      options: ["Une pigmentation jaune", "Une rougeur cutanée", "Une infection par champignon", "Une ulcération"],
      correct: 1,
      type: 'single'
    },
    {
      id: 'q23',
      question: "L'escarre correspond à :",
      options: ["Une ulcération cutanée liée à la pression", "Une infection par champignon", "Un bouton rouge bénin", "Une rougeur qui disparaît à la pression"],
      correct: 0,
      type: 'single'
    },
    {
      id: 'q24',
      question: "Le prurit est :",
      options: ["Une rougeur cutanée", "Une démangeaison", "Une lésion en relief", "Une desquamation"],
      correct: 1,
      type: 'single'
    },
    {
      id: 'q25',
      question: "Une mycose est causée par :",
      options: ["Une bactérie", "Un virus", "Un champignon", "Un parasite"],
      correct: 2,
      type: 'single'
    },
    {
      id: 'q26',
      question: "Que signifie le suffixe « -ite » ?",
      options: ["Ablation", "Inflammation", "Douleur", "Tumeur"],
      correct: 1,
      type: 'single'
    },
    {
      id: 'q27',
      question: "Que signifie le préfixe « néphro- » ?",
      options: ["Cœur", "Rein", "Poumon", "Foie"],
      correct: 1,
      type: 'single'
    },
    {
      id: 'q28',
      question: "Le terme « hématémèse » signifie :",
      options: ["Vomissement de sang", "Sang dans les urines", "Sang dans les selles", "Sang dans les crachats"],
      correct: 0,
      type: 'single'
    },
    {
      id: 'q29',
      question: "Une hémoptysie est :",
      options: ["Du sang dans les selles", "Du sang dans les crachats", "Du sang dans les urines", "Vomissement de sang"],
      correct: 1,
      type: 'single'
    },
    {
      id: 'q30',
      question: "L'ictère correspond à :",
      options: ["Coloration jaune de la peau", "Coloration bleutée de la peau", "Coloration rouge de la peau", "Coloration pâle de la peau"],
      correct: 0,
      type: 'single'
    }
  ];

  const handleTextToSpeech = async () => {
    if (isPlaying) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setIsPlaying(false);
      return;
    }

    const currentSlideData = slides[currentSlide];
    let textToSpeak = '';

    if (currentSlideData.type === 'title') {
      textToSpeak = `${currentSlideData.title}. ${currentSlideData.subtitle}`;
    } else if (currentSlideData.type === 'section') {
      textToSpeak = currentSlideData.title + (currentSlideData.subtitle ? `. ${currentSlideData.subtitle}` : '');
    } else if (currentSlideData.type === 'sommaire') {
      textToSpeak = `Sommaire du cours. Section 1 : Paramètres à mesurer, comprenant température, tension artérielle, fréquence cardiaque, saturation, fréquence respiratoire et glycémie. Section 2 : Paramètres à observer, comprenant état cutané, poids, selles, urines et douleur. Section 3 : Vocabulaire médical par appareil.`;
    } else if (currentSlideData.type === 'content') {
      textToSpeak = currentSlideData.title;
      if (currentSlideData.definition) textToSpeak += `. Définition : ${currentSlideData.definition}`;
      if (currentSlideData.content) textToSpeak += `. ${currentSlideData.content}`;
      if (currentSlideData.extraInfo) {
        currentSlideData.extraInfo.forEach(info => {
          textToSpeak += `. ${info.term} : ${info.desc}`;
        });
      }
      if (currentSlideData.warning) textToSpeak += `. Attention : ${currentSlideData.warning}`;
      if (currentSlideData.highlight) textToSpeak += `. ${currentSlideData.highlight}`;
    } else if (currentSlideData.type === 'table') {
      textToSpeak = currentSlideData.title;
      if (currentSlideData.subtitle) textToSpeak += `. ${currentSlideData.subtitle}`;
      currentSlideData.tableData?.forEach(row => {
        textToSpeak += `. ${row.label} : ${row.value}`;
      });
      currentSlideData.alerts?.forEach(alert => {
        textToSpeak += `. ${alert}`;
      });
      currentSlideData.risks?.forEach(risk => {
        textToSpeak += `. Risque : ${risk}`;
      });
    } else if (currentSlideData.type === 'list') {
      textToSpeak = currentSlideData.title;
      currentSlideData.items?.forEach(item => {
        textToSpeak += `. ${item.term} : ${item.desc}`;
      });
    } else if (currentSlideData.type === 'recap') {
      textToSpeak = `${currentSlideData.title}. Paramètres à mesurer : ${currentSlideData.mesurer.join(', ')}. Paramètres à observer : ${currentSlideData.observer.join(', ')}.`;
    } else if (currentSlideData.type === 'vocabulary') {
      textToSpeak = currentSlideData.title;
      currentSlideData.columns?.forEach(col => {
        textToSpeak += `. ${col.title} : `;
        col.items.forEach(item => {
          textToSpeak += `${item.term} signifie ${item.desc}. `;
        });
      });
    } else if (currentSlideData.type === 'medicalVocab') {
      textToSpeak = currentSlideData.title;
      textToSpeak += `. Anatomie : ${currentSlideData.anatomy}`;
      textToSpeak += `. Termes principaux : `;
      currentSlideData.terms?.forEach(t => {
        textToSpeak += `${t.term} : ${t.def}. `;
      });
      textToSpeak += `. Anomalies : `;
      currentSlideData.anomalies?.forEach(a => {
        textToSpeak += `${a.term} : ${a.def}. `;
      });
    } else if (currentSlideData.type === 'vocabRecap') {
      textToSpeak = currentSlideData.title;
      currentSlideData.categories?.forEach(cat => {
        textToSpeak += `. ${cat.name} : ${cat.terms.join(', ')}`;
      });
    } else if (currentSlideData.type === 'congratulations') {
      textToSpeak = `${currentSlideData.title}. ${currentSlideData.message}. ${currentSlideData.subtitle}. ${currentSlideData.details?.join('. ')}. ${currentSlideData.nextStep}`;
    }

    // Replace abbreviations
    textToSpeak = textToSpeak
      .replace(/AP/g, 'auxiliaire de puériculture')
      .replace(/bpm/g, 'battements par minute')
      .replace(/mvts/g, 'mouvements')
      .replace(/mmHg/g, 'millimètres de mercure')
      .replace(/g\/L/g, 'grammes par litre')
      .replace(/O2/g, 'oxygène')
      .replace(/AVC/g, 'accident vasculaire cérébral')
      .replace(/AIT/g, 'accident ischémique transitoire')
      .replace(/IMC/g, 'indice de masse corporelle');

    // Limit text length for faster TTS response
    if (textToSpeak.length > 800) {
      textToSpeak = textToSpeak.substring(0, 800) + '...';
    }

    // Don't call TTS if no text
    if (!textToSpeak || textToSpeak.trim() === '') {
      toast.info("Pas de contenu audio pour cette slide");
      setIsPlaying(false);
      return;
    }

    try {
      setIsLoading(true);
      setIsPlaying(true);
      const API_URL = window.location.origin || '';
      const response = await fetch(`${API_URL}/api/tts/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: textToSpeak, speed: 1.1 })
      });

      if (!response.ok) throw new Error('TTS failed');

      const data = await response.json();
      const audioData = data.audio_base64 || data.audio;
      
      setIsLoading(false);
      
      if (audioData) {
        const audioBlob = new Blob(
          [Uint8Array.from(atob(audioData), c => c.charCodeAt(0))],
          { type: 'audio/mp3' }
        );
        const audioUrl = URL.createObjectURL(audioBlob);
        audioRef.current = new Audio(audioUrl);
        audioRef.current.onended = () => setIsPlaying(false);
        audioRef.current.onerror = () => {
          toast.error("Erreur de lecture audio");
          setIsPlaying(false);
        };
        audioRef.current.play().catch(err => {
          console.error('Audio play error:', err);
          toast.error("Impossible de lire l'audio");
          setIsPlaying(false);
        });
      } else {
        toast.error("Pas de données audio reçues");
        setIsPlaying(false);
      }
    } catch (error) {
      console.error('TTS error:', error);
      toast.error("Erreur lors de la génération audio");
      setIsPlaying(false);
      setIsLoading(false);
    }
  };

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      setShowQuiz(true);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const handleQuizAnswer = (questionId, answerIndex) => {
    if (!quizSubmitted) {
      setQuizAnswers({ ...quizAnswers, [questionId]: answerIndex });
    }
  };

  const submitQuiz = () => {
    let score = 0;
    quizQuestions.forEach(q => {
      if (quizAnswers[q.id] === q.correct) score++;
    });
    setQuizScore(score);
    setQuizSubmitted(true);
    
    const percentage = (score / quizQuestions.length) * 100;
    if (percentage >= 70) {
      toast.success(`Félicitations ! Vous avez obtenu ${score}/${quizQuestions.length} (${percentage.toFixed(0)}%)`);
    } else {
      toast.error(`Score : ${score}/${quizQuestions.length} (${percentage.toFixed(0)}%). Il faut au moins 70% pour valider.`);
    }
  };

  const renderSlide = () => {
    const slide = slides[currentSlide];

    if (slide.type === 'title') {
      return (
        <div style={{
          background: slide.gradient,
          minHeight: '500px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '3rem',
          color: 'white',
          textAlign: 'center'
        }}>
          <h1 style={{ fontSize: '3.5rem', fontWeight: 'bold', marginBottom: '1.5rem' }}>{slide.title}</h1>
          <p style={{ fontSize: '1.5rem', maxWidth: '800px', opacity: 0.95, lineHeight: '1.6' }}>{slide.subtitle}</p>
        </div>
      );
    }

    if (slide.type === 'section') {
      return (
        <div style={{
          background: slide.gradient,
          minHeight: '500px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '3rem',
          color: 'white',
          textAlign: 'center'
        }}>
          <h2 style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>{slide.title}</h2>
          {slide.subtitle && <p style={{ fontSize: '1.3rem', opacity: 0.9 }}>{slide.subtitle}</p>}
        </div>
      );
    }

    if (slide.type === 'content') {
      return (
        <div style={{ padding: '2rem', minHeight: '500px', background: 'white' }}>
          <h2 style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#3B82F6', marginBottom: '1.5rem' }}>{slide.title}</h2>
          <div style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start' }}>
            {slide.image && (
              <img src={slide.image} alt="" style={{ width: '280px', height: '200px', objectFit: 'cover', borderRadius: '0.75rem', flexShrink: 0 }} />
            )}
            <div style={{ flex: 1 }}>
              {slide.definition && (
                <div style={{ background: '#EFF6FF', padding: '1rem', borderRadius: '0.5rem', marginBottom: '1rem', borderLeft: '4px solid #3B82F6' }}>
                  <strong>Définition :</strong> {slide.definition}
                </div>
              )}
              {slide.content && <p style={{ fontSize: '1.1rem', lineHeight: '1.8', marginBottom: '1rem', color: '#374151' }}>{slide.content}</p>}
              {slide.extraInfo && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
                  {slide.extraInfo.map((info, idx) => (
                    <div key={idx} style={{ 
                      background: '#F0FDF4', 
                      padding: '0.75rem 1rem', 
                      borderRadius: '0.5rem', 
                      borderLeft: '4px solid #22C55E'
                    }}>
                      <strong style={{ color: '#166534' }}>{info.term} :</strong>{' '}
                      <span style={{ color: '#374151' }}>{info.desc}</span>
                    </div>
                  ))}
                </div>
              )}
              {slide.highlight && (
                <div style={{ background: '#FEF3C7', padding: '1rem', borderRadius: '0.5rem', marginBottom: '1rem', borderLeft: '4px solid #F59E0B' }}>
                  {slide.highlight}
                </div>
              )}
              {slide.warning && (
                <div style={{ background: '#FEE2E2', padding: '1rem', borderRadius: '0.5rem', borderLeft: '4px solid #EF4444', color: '#991B1B' }}>
                  <strong>Attention :</strong> {slide.warning}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (slide.type === 'table') {
      return (
        <div style={{ padding: '2rem', minHeight: '500px', background: 'white' }}>
          <h2 style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#3B82F6', marginBottom: '0.5rem' }}>{slide.title}</h2>
          {slide.subtitle && <p style={{ color: '#6B7280', marginBottom: '1.5rem' }}>{slide.subtitle}</p>}
          <div style={{ display: 'grid', gap: '0.75rem', marginBottom: '1.5rem' }}>
            {slide.tableData?.map((row, idx) => (
              <div key={idx} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '1rem 1.5rem',
                background: `${row.color}15`,
                borderRadius: '0.5rem',
                borderLeft: `4px solid ${row.color}`
              }}>
                <span style={{ fontWeight: '500', color: '#374151' }}>{row.label}</span>
                <span style={{ fontWeight: '600', color: row.color, fontSize: '1.1rem' }}>{row.value}</span>
              </div>
            ))}
          </div>
          {slide.alerts && (
            <div style={{ background: '#FEF3C7', padding: '1rem', borderRadius: '0.5rem', borderLeft: '4px solid #F59E0B' }}>
              <strong>Alertes :</strong>
              <ul style={{ margin: '0.5rem 0 0 1.5rem', padding: 0 }}>
                {slide.alerts.map((alert, idx) => <li key={idx} style={{ marginBottom: '0.25rem' }}>{alert}</li>)}
              </ul>
            </div>
          )}
          {slide.risks && (
            <div style={{ background: '#FEE2E2', padding: '1rem', borderRadius: '0.5rem', borderLeft: '4px solid #EF4444' }}>
              <strong>Risques :</strong>
              <ul style={{ margin: '0.5rem 0 0 1.5rem', padding: 0 }}>
                {slide.risks.map((risk, idx) => <li key={idx} style={{ marginBottom: '0.25rem' }}>{risk}</li>)}
              </ul>
            </div>
          )}
        </div>
      );
    }

    if (slide.type === 'list') {
      return (
        <div style={{ padding: '2rem', minHeight: '500px', background: 'white' }}>
          <h2 style={{ fontSize: '1.8rem', fontWeight: 'bold', color: slide.alertColor || '#3B82F6', marginBottom: '1.5rem' }}>{slide.title}</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {slide.items?.map((item, idx) => (
              <div key={idx} style={{
                padding: '1.25rem',
                background: `${slide.alertColor || '#3B82F6'}10`,
                borderRadius: '0.75rem',
                borderLeft: `4px solid ${slide.alertColor || '#3B82F6'}`
              }}>
                <strong style={{ color: slide.alertColor || '#3B82F6', fontSize: '1.1rem' }}>{item.term}</strong>
                <p style={{ marginTop: '0.5rem', color: '#4B5563', lineHeight: '1.6' }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (slide.type === 'recap') {
      return (
        <div style={{ padding: '2rem', minHeight: '500px', background: 'linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 100%)' }}>
          <h2 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1E40AF', marginBottom: '2rem', textAlign: 'center' }}>{slide.title}</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
            <div style={{ background: '#DC262615', padding: '1.5rem', borderRadius: '1rem', border: '2px solid #DC2626' }}>
              <h3 style={{ color: '#DC2626', marginBottom: '1rem', fontSize: '1.3rem' }}>6 Paramètres à MESURER</h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {slide.mesurer.map((item, idx) => (
                  <li key={idx} style={{ padding: '0.5rem 0', borderBottom: '1px solid #DC262630', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ color: '#DC2626' }}>•</span> {item}
                  </li>
                ))}
              </ul>
            </div>
            <div style={{ background: '#05966915', padding: '1.5rem', borderRadius: '1rem', border: '2px solid #059669' }}>
              <h3 style={{ color: '#059669', marginBottom: '1rem', fontSize: '1.3rem' }}>5 Paramètres à OBSERVER</h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {slide.observer.map((item, idx) => (
                  <li key={idx} style={{ padding: '0.5rem 0', borderBottom: '1px solid #05966930', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ color: '#059669' }}>•</span> {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      );
    }

    if (slide.type === 'vocabulary') {
      return (
        <div style={{ padding: '2rem', minHeight: '500px', background: 'linear-gradient(135deg, #FDF4FF 0%, #FAE8FF 100%)' }}>
          <h2 style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#7C3AED', marginBottom: '1.5rem', textAlign: 'center' }}>{slide.title}</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem' }}>
            {slide.columns?.map((column, colIdx) => (
              <div key={colIdx} style={{ 
                background: 'white', 
                padding: '1.25rem', 
                borderRadius: '1rem', 
                boxShadow: '0 4px 12px rgba(124, 58, 237, 0.1)',
                border: '1px solid #E9D5FF'
              }}>
                <h3 style={{ 
                  color: '#7C3AED', 
                  marginBottom: '1rem', 
                  fontSize: '1.1rem', 
                  fontWeight: '600',
                  borderBottom: '2px solid #E9D5FF',
                  paddingBottom: '0.5rem'
                }}>{column.title}</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {column.items.map((item, itemIdx) => (
                    <div key={itemIdx} style={{ 
                      padding: '0.5rem 0.75rem', 
                      background: '#F5F3FF',
                      borderRadius: '0.5rem',
                      fontSize: '0.9rem'
                    }}>
                      <strong style={{ color: '#6D28D9' }}>{item.term}</strong>
                      <span style={{ color: '#6B7280' }}> : {item.desc}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (slide.type === 'sommaire') {
      // Define slide targets for each section
      const slideTargets = {
        '1': 4,   // Paramètres à mesurer -> slide 5 (index 4)
        '2': 19,  // Paramètres à observer -> slide 20 (index 19)
        '3': 26   // Vocabulaire médical -> slide 27 (index 26)
      };
      
      const handleSectionClick = (sectionNum) => {
        const targetSlide = slideTargets[sectionNum];
        if (targetSlide !== undefined) {
          setCurrentSlide(targetSlide);
        }
      };
      
      return (
        <div style={{ padding: '2rem', minHeight: '500px', background: 'linear-gradient(135deg, #F8FAFC 0%, #E2E8F0 100%)' }}>
          <h2 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1E40AF', marginBottom: '1rem', textAlign: 'center' }}>{slide.title}</h2>
          <p style={{ textAlign: 'center', color: '#6B7280', marginBottom: '1.5rem', fontSize: '0.95rem' }}>Cliquez sur une section pour y accéder directement</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem' }}>
            {slide.sections?.map((section, idx) => (
              <div 
                key={idx}
                data-testid={`sommaire-section-${section.num}`}
                onClick={() => handleSectionClick(section.num)}
                style={{
                  background: 'white',
                  borderRadius: '1rem',
                  overflow: 'hidden',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  border: `3px solid ${section.color}`,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.03)';
                  e.currentTarget.style.boxShadow = `0 8px 24px ${section.color}40`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                }}
              >
                <div style={{ 
                  background: section.color, 
                  padding: '1rem', 
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem'
                }}>
                  <span style={{ 
                    background: 'white', 
                    color: section.color, 
                    width: '32px', 
                    height: '32px', 
                    borderRadius: '50%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    fontWeight: 'bold'
                  }}>{section.num}</span>
                  <span style={{ fontWeight: '600' }}>{section.title}</span>
                </div>
                <div style={{ padding: '1rem' }}>
                  {section.items.map((item, itemIdx) => (
                    <div key={itemIdx} style={{ 
                      padding: '0.4rem 0', 
                      borderBottom: itemIdx < section.items.length - 1 ? '1px solid #E5E7EB' : 'none',
                      fontSize: '0.9rem',
                      color: '#374151'
                    }}>
                      • {item}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (slide.type === 'medicalVocab') {
      return (
        <div style={{ padding: '1.5rem', minHeight: '500px', background: `linear-gradient(135deg, ${slide.color}10 0%, ${slide.color}05 100%)` }}>
          <h2 style={{ fontSize: '1.6rem', fontWeight: 'bold', color: slide.color, marginBottom: '0.75rem' }}>{slide.title}</h2>
          <div style={{ 
            background: `${slide.color}15`, 
            padding: '0.75rem 1rem', 
            borderRadius: '0.5rem', 
            marginBottom: '1rem',
            borderLeft: `4px solid ${slide.color}`,
            fontSize: '0.95rem'
          }}>
            <strong>Anatomie :</strong> {slide.anatomy}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div style={{ background: 'white', padding: '1rem', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
              <h3 style={{ color: slide.color, marginBottom: '0.75rem', fontSize: '1rem', fontWeight: '600' }}>Termes principaux</h3>
              <div style={{ display: 'grid', gap: '0.4rem' }}>
                {slide.terms?.map((t, idx) => (
                  <div key={idx} style={{ fontSize: '0.85rem', padding: '0.3rem 0', borderBottom: '1px solid #F3F4F6' }}>
                    <strong style={{ color: slide.color }}>{t.term}</strong> : <span style={{ color: '#6B7280' }}>{t.def}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div style={{ background: 'white', padding: '1rem', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', marginBottom: '0.75rem' }}>
                <h3 style={{ color: '#EF4444', marginBottom: '0.5rem', fontSize: '1rem', fontWeight: '600' }}>Anomalies</h3>
                <div style={{ display: 'grid', gap: '0.3rem' }}>
                  {slide.anomalies?.map((a, idx) => (
                    <div key={idx} style={{ fontSize: '0.85rem', padding: '0.2rem 0' }}>
                      <strong style={{ color: '#EF4444' }}>{a.term}</strong> : <span style={{ color: '#6B7280' }}>{a.def}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ background: `${slide.color}10`, padding: '0.75rem', borderRadius: '0.5rem', border: `1px solid ${slide.color}30` }}>
                <h3 style={{ color: slide.color, marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '600' }}>Pathologies</h3>
                <div style={{ fontSize: '0.8rem', color: '#4B5563' }}>
                  {slide.pathologies?.map((p, idx) => (
                    <span key={idx}>{p}{idx < slide.pathologies.length - 1 ? ' • ' : ''}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (slide.type === 'vocabRecap') {
      return (
        <div style={{ padding: '2rem', minHeight: '500px', background: 'linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 100%)' }}>
          <h2 style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#1E40AF', marginBottom: '1.5rem', textAlign: 'center' }}>{slide.title}</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '1rem' }}>
            {slide.categories?.map((cat, idx) => (
              <div key={idx} style={{
                background: 'white',
                borderRadius: '0.75rem',
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                border: `2px solid ${cat.color}`
              }}>
                <div style={{ background: cat.color, padding: '0.75rem', color: 'white', textAlign: 'center', fontWeight: '600', fontSize: '0.9rem' }}>
                  {cat.name}
                </div>
                <div style={{ padding: '0.75rem' }}>
                  {cat.terms.map((term, tIdx) => (
                    <div key={tIdx} style={{ 
                      fontSize: '0.8rem', 
                      padding: '0.3rem 0',
                      borderBottom: tIdx < cat.terms.length - 1 ? '1px solid #F3F4F6' : 'none',
                      color: '#374151'
                    }}>
                      {term}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (slide.type === 'congratulations') {
      return (
        <div style={{ 
          minHeight: '500px', 
          background: 'linear-gradient(135deg, #10B981 0%, #059669 50%, #047857 100%)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '3rem',
          color: 'white',
          textAlign: 'center'
        }}>
          <div style={{ 
            fontSize: '5rem', 
            marginBottom: '1rem',
            animation: 'pulse 2s infinite'
          }}>
            🎉
          </div>
          <h1 style={{ 
            fontSize: '3rem', 
            fontWeight: 'bold', 
            marginBottom: '1rem',
            textShadow: '0 2px 10px rgba(0,0,0,0.2)'
          }}>
            {slide.title}
          </h1>
          <p style={{ 
            fontSize: '1.5rem', 
            marginBottom: '0.5rem',
            opacity: 0.95
          }}>
            {slide.message}
          </p>
          <p style={{ 
            fontSize: '1.1rem', 
            marginBottom: '2rem',
            opacity: 0.85,
            maxWidth: '600px'
          }}>
            {slide.subtitle}
          </p>
          <div style={{ 
            display: 'flex', 
            gap: '1rem', 
            marginBottom: '2rem',
            flexWrap: 'wrap',
            justifyContent: 'center'
          }}>
            {slide.details?.map((detail, idx) => (
              <div key={idx} style={{
                background: 'rgba(255,255,255,0.2)',
                padding: '0.75rem 1.5rem',
                borderRadius: '2rem',
                fontSize: '1rem',
                backdropFilter: 'blur(10px)'
              }}>
                ✓ {detail}
              </div>
            ))}
          </div>
          <div style={{
            background: 'rgba(255,255,255,0.95)',
            color: '#059669',
            padding: '1rem 2rem',
            borderRadius: '1rem',
            fontSize: '1.1rem',
            fontWeight: '600',
            boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
          }}>
            {slide.nextStep}
          </div>
        </div>
      );
    }

    return null;
  };

  // Quiz view
  if (showQuiz) {
    return (
      <div style={{ background: '#f8fafc', borderRadius: '1rem', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
        <div style={{ background: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)', padding: '1.5rem 2rem', color: 'white' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>Quiz - Activité 3 : Paramètres vitaux</h2>
          <p style={{ opacity: 0.9 }}>{quizQuestions.length} questions</p>
        </div>

        <div style={{ padding: '2rem', maxHeight: '600px', overflowY: 'auto' }}>
          {quizQuestions.map((q, qIdx) => (
            <div key={q.id} style={{ marginBottom: '2rem', padding: '1.5rem', background: 'white', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
              <p style={{ fontWeight: '600', marginBottom: '1rem', fontSize: '1.05rem' }}>
                {qIdx + 1}. {q.question}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {q.options.map((opt, optIdx) => {
                  const isSelected = quizAnswers[q.id] === optIdx;
                  const isCorrect = optIdx === q.correct;
                  let bg = 'white';
                  let border = '1px solid #E5E7EB';
                  
                  if (quizSubmitted) {
                    if (isCorrect) {
                      bg = '#D1FAE5';
                      border = '2px solid #10B981';
                    } else if (isSelected && !isCorrect) {
                      bg = '#FEE2E2';
                      border = '2px solid #EF4444';
                    }
                  } else if (isSelected) {
                    bg = '#DBEAFE';
                    border = '2px solid #3B82F6';
                  }

                  return (
                    <label key={optIdx} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.75rem 1rem',
                      background: bg,
                      border: border,
                      borderRadius: '0.5rem',
                      cursor: quizSubmitted ? 'default' : 'pointer',
                      transition: 'all 0.2s'
                    }}>
                      <input
                        type="radio"
                        name={q.id}
                        checked={isSelected}
                        onChange={() => handleQuizAnswer(q.id, optIdx)}
                        disabled={quizSubmitted}
                        style={{ accentColor: '#3B82F6' }}
                      />
                      <span>{opt}</span>
                      {quizSubmitted && isCorrect && <CheckCircle size={18} color="#10B981" style={{ marginLeft: 'auto' }} />}
                      {quizSubmitted && isSelected && !isCorrect && <XCircle size={18} color="#EF4444" style={{ marginLeft: 'auto' }} />}
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div style={{ padding: '1rem 2rem', background: '#f1f5f9', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button
            onClick={() => { setShowQuiz(false); setCurrentSlide(0); setQuizAnswers({}); setQuizSubmitted(false); }}
            style={{ padding: '0.75rem 1.5rem', background: '#6B7280', color: 'white', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: '500' }}
          >
            Revoir le cours
          </button>
          
          {quizSubmitted ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <span style={{ fontSize: '1.25rem', fontWeight: 'bold', color: quizScore >= quizQuestions.length * 0.7 ? '#10B981' : '#EF4444' }}>
                Score : {quizScore}/{quizQuestions.length} ({((quizScore/quizQuestions.length)*100).toFixed(0)}%)
              </span>
              <button
                onClick={() => { setQuizAnswers({}); setQuizSubmitted(false); }}
                style={{ padding: '0.75rem 1.5rem', background: '#3B82F6', color: 'white', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: '500' }}
              >
                Recommencer
              </button>
            </div>
          ) : (
            <button
              onClick={submitQuiz}
              disabled={Object.keys(quizAnswers).length < quizQuestions.length}
              style={{
                padding: '0.75rem 1.5rem',
                background: Object.keys(quizAnswers).length < quizQuestions.length ? '#9CA3AF' : '#10B981',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                cursor: Object.keys(quizAnswers).length < quizQuestions.length ? 'not-allowed' : 'pointer',
                fontWeight: '500'
              }}
            >
              Valider ({Object.keys(quizAnswers).length}/{quizQuestions.length})
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: '#f8fafc', borderRadius: '1rem', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
      {/* Progress bar */}
      <div style={{ height: '4px', background: '#e2e8f0' }}>
        <div style={{
          height: '100%',
          background: 'linear-gradient(90deg, #3B82F6, #1D4ED8)',
          width: `${((currentSlide + 1) / slides.length) * 100}%`,
          transition: 'width 0.3s ease'
        }} />
      </div>

      {/* Slide content */}
      {renderSlide()}

      {/* Controls */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1rem 2rem',
        background: '#f1f5f9',
        borderTop: '1px solid #e2e8f0'
      }}>
        <button
          onClick={prevSlide}
          disabled={currentSlide === 0}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.75rem 1.5rem',
            background: currentSlide === 0 ? '#e2e8f0' : '#3B82F6',
            color: currentSlide === 0 ? '#94a3b8' : 'white',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: currentSlide === 0 ? 'not-allowed' : 'pointer',
            fontWeight: '500'
          }}
        >
          <ChevronLeft size={20} />
          Précédent
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button
            onClick={handleTextToSpeech}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem',
              background: isPlaying ? '#3B82F6' : '#f8fafc',
              color: isPlaying ? 'white' : '#3B82F6',
              border: '2px solid #3B82F6',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            {isLoading ? <Loader2 size={20} className="animate-spin" /> : isPlaying ? <VolumeX size={20} /> : <Volume2 size={20} />}
            {isLoading ? 'Chargement...' : isPlaying ? 'Arrêter' : 'Écouter'}
          </button>

          <span style={{
            padding: '0.5rem 1rem',
            background: '#3B82F6',
            color: 'white',
            borderRadius: '1rem',
            fontSize: '0.875rem',
            fontWeight: '500'
          }}>
            {currentSlide + 1} / {slides.length}
          </span>
        </div>

        <button
          onClick={nextSlide}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.75rem 1.5rem',
            background: '#3B82F6',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            fontWeight: '500'
          }}
        >
          {currentSlide === slides.length - 1 ? 'Quiz' : 'Suivant'}
          <ChevronRight size={20} />
        </button>
      </div>
    </div>
  );
};

export default DEAPActivite3Presentation;
